/**
 * Staying fresh without websockets.
 *
 * Telegram minimises the Mini App rather than closing it, so "reopening the
 * app" is usually the same webview coming back on screen with everything it
 * showed an hour ago. Sockets would solve this at the price of reconnect
 * logic for a webview that is suspended all day and a server that redeploys
 * under the connections; the events here are rare and the choir is small, so
 * the app asks instead. Two primitives cover it: a poll that runs while the app
 * is on screen, asks at once when it returns, and asks its first question
 * whatever the webview says about itself, and a plain wake subscription for
 * data that is loaded once and only needs re-reading on return.
 */

import { useEffect, useRef } from 'react';

/**
 * The webview came back on screen. Deduped by time because platforms disagree
 * on what returning fires - iOS sends visibilitychange and focus together -
 * and one wake must not become two reloads.
 */
function subscribeToWake(handler: () => void): () => void {
  let lastWake = 0;
  const wake = () => {
    if (document.visibilityState !== 'visible') return;
    const now = Date.now();
    if (now - lastWake < 1000) return;
    lastWake = now;
    handler();
  };
  document.addEventListener('visibilitychange', wake);
  window.addEventListener('focus', wake);
  return () => {
    document.removeEventListener('visibilitychange', wake);
    window.removeEventListener('focus', wake);
  };
}

/**
 * How long a call may be in flight before the next tick stops waiting for it.
 *
 * Longer than the timeout every request carries, so in the ordinary case the
 * abort is what frees the poll and this is only the net underneath: it also
 * catches an `fn` that hangs for a reason of its own.
 */
export const STALE_MS = 20_000;

/** The two things the guard asks the browser, so that a test can answer for it. */
export type Surroundings = { now: () => number; visible: () => boolean };

const THE_BROWSER: Surroundings = {
  now: () => Date.now(),
  visible: () => document.visibilityState === 'visible',
};

/**
 * One call at a time, and never more than one - the rule that decides whether
 * a tick asks the server or turns back at the door.
 *
 * Its own function rather than four lines inside the hook because it is the
 * part with a bug worth a test: a request that never settles - what a webview
 * frozen mid-poll leaves behind, and what a request with no timeout could
 * become - used to raise a boolean that nothing then lowered. From that moment
 * the screen followed nothing: every tick and every wake turned back. The panel
 * looked cured by leaving the tab and returning only because that mounts it
 * afresh, and the dot on the tab bar never came back at all, since App's poll
 * is mounted once for the life of the app. Hence a deadline, which cannot latch.
 *
 * `force` skips the screen test and nothing else - the deadline still holds, so
 * it cannot pile a second request on a first. It is for the one ask that is not
 * polling: the screen has just been mounted, somebody is looking at it, and a
 * single request is not what «do not work in the background» is protecting.
 * Owner's call, 2026-09-03, after a webview was found reporting
 * `visibilityState: "hidden"` while open and focused - Claude Code's browser
 * pane does it - where gating the first ask left the regent's panel on «Смотрю,
 * идёт ли что-нибудь…» with nothing able to bring it out: the wake subscription
 * tests the same lie before it fires, so it never fired either.
 */
export function createTicker(
  fn: () => Promise<unknown>,
  surroundings: Surroundings = THE_BROWSER,
): (force?: boolean) => void {
  // The call in flight, by number and by when it started. Numbered because an
  // abandoned call may still settle later, and it must not clear the guard
  // belonging to the one that replaced it.
  let attempts = 0;
  let inFlight: number | null = null;
  let startedAt = 0;
  return (force = false) => {
    if (!force && !surroundings.visible()) return;
    if (inFlight !== null && surroundings.now() - startedAt < STALE_MS) return;
    const attempt = ++attempts;
    inFlight = attempt;
    startedAt = surroundings.now();
    void fn().finally(() => {
      if (inFlight === attempt) inFlight = null;
    });
  };
}

/**
 * Run `fn` now, every `intervalMs` while the app is on screen, and once more
 * the moment it comes back - which is what makes a poll double as the
 * "refresh on reopening" everybody actually asked for.
 *
 * «Now» means now: the first ask goes out whether or not the webview admits to
 * being on screen. Everything after it is gated as it was.
 *
 * The ref keeps the interval from restarting when a render hands in a new
 * closure; the ticker keeps a slow network from piling requests on itself.
 */
export function usePoll(fn: () => Promise<unknown>, intervalMs: number, enabled = true): void {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    if (!enabled) return;
    const tick = createTicker(() => fnRef.current());
    tick(true);
    // Wrapped rather than passed on: `setInterval` hands its callback nothing,
    // but a listener would hand it an event, and an event is truthy.
    const timer = window.setInterval(() => tick(), intervalMs);
    const unsubscribe = subscribeToWake(() => tick());
    return () => {
      window.clearInterval(timer);
      unsubscribe();
    };
  }, [intervalMs, enabled]);
}

/** Call `onWake` when the webview returns to the screen. Not on mount. */
export function useWake(onWake: () => void): void {
  const handlerRef = useRef(onWake);
  handlerRef.current = onWake;

  useEffect(() => subscribeToWake(() => handlerRef.current()), []);
}
